import { IFilter, IPageInfo } from '@/interfaces/common.interface';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Custom hook to manage filters, searching, and pagination state.
 * @param initialFilters Initial filter state.
 */
export const useFilter = <T extends IFilter>(initialFilters: T) => {
    const [filters, setFilters] = useState<T>(initialFilters);
    const [debouncedFilters, setDebouncedFilters] = useState<T>(initialFilters);
    const [pageInfo, setPageInfoState] = useState<IPageInfo>({
        limit: initialFilters.limit || 10,
        page: initialFilters.page || 1,
        total: 0,
        totalPages: 0,
    });
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const prevSearchRef = useRef<string | undefined>(initialFilters.search);

    /**
     * Effect to synchronize filters with debouncedFilters.
     * Updates 'search' with a delay, but other filters (page, isActive, etc.) immediately.
     */
    useEffect(() => {
        const isSearchChanged = filters.search !== prevSearchRef.current;
        prevSearchRef.current = filters.search;

        if (isSearchChanged) {
            // If search changed, debounce it
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => {
                setDebouncedFilters(filters);
            }, 500); // 500ms delay for search
        } else {
            // If other filters changed, update immediately
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            setDebouncedFilters(filters);
        }

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [filters]);

    /**
     * Updates one or more filter fields.
     * Automatically resets the page to 1 when any non-pagination filter is changed.
     */
    const updateFilter = useCallback((updates: Partial<T>) => {
        setFilters((prev) => {
            const nextFilters = { ...prev, ...updates };

            // Check if any filter other than 'page' or 'limit' has changed
            const keys = Object.keys(updates) as (keyof T)[];
            const hasNonPaginationChanges = keys.some(key => key !== 'page' && key !== 'limit');

            if (hasNonPaginationChanges) {
                nextFilters.page = 1 as any; // Reset to first page
            }

            return nextFilters;
        });
    }, []);

    /**
     * Resets filters to their initial state.
     */
    const resetFilters = useCallback(() => {
        setFilters(initialFilters);
    }, [initialFilters]);

    /**
     * Conveniently update the page number.
     */
    const handlePageChange = useCallback((page: number) => {
        setFilters((prev) => ({ ...prev, page: page }));
    }, []);

    /**
     * Updates the pagination information (total items, total pages) from an API response.
     */
    const setPageInfo = useCallback((data: IPageInfo) => {
        setPageInfoState(data);
    }, []);

    const handlePageSizeChange = useCallback((limit: number) => {
        setFilters((prev) => ({ ...prev, limit, page: 1 }));
    }, []);

    return {
        filters,
        setFilters,
        debouncedFilters,
        updateFilter,
        resetFilters,
        handlePageChange,
        pageInfo,
        setPageInfo,
        handlePageSizeChange,
    };
};
